import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import type {
  EventConfiguration,
  EventConfigurationBase,
  EventConfigurationUsagesResponse,
} from './types';

const customEventsApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createEventConfiguration: builder.mutation<
      EventConfiguration,
      EventConfigurationBase
    >({
      invalidatesTags: [
        { id: 'LIST', type: 'CustomEventConfigurations' },
        'Tools',
        'AutoflowWorkflow',
      ],
      query: body => ({
        body,
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/event-configuration`,
      }),
    }),
    deleteEventConfiguration: builder.mutation<void, string>({
      invalidatesTags: (result, error, id) => [
        { id, type: 'CustomEventConfigurations' },
        'Tools',
        'AutoflowWorkflow',
      ],
      query: event_configuration_id => ({
        method: 'DELETE',
        url: `${SOLVE_V2_BASE_URL}/event-configuration/${event_configuration_id}`,
      }),
    }),
    duplicateEventConfiguration: builder.mutation<EventConfiguration, string>({
      invalidatesTags: [
        { id: 'LIST', type: 'CustomEventConfigurations' },
        'Tools',
        'AutoflowWorkflow',
      ],
      query: event_configuration_id => ({
        method: 'POST',
        url: `${SOLVE_V2_BASE_URL}/event-configuration/${event_configuration_id}/duplicate`,
      }),
    }),
    getEventConfigurationById: builder.query<EventConfiguration, string>({
      providesTags: (result, error, id) => [
        { id, type: 'CustomEventConfigurations' },
      ],
      query: event_configuration_id => ({
        url: `${SOLVE_V2_BASE_URL}/event-configuration/${event_configuration_id}`,
      }),
    }),
    getEventConfigurations: builder.query<
      EventConfiguration[],
      { draft_mode: 'published' | 'draft' | 'any' }
    >({
      providesTags: result =>
        result
          ? [
              ...result.map(
                ({ event_configuration_id }) =>
                  ({
                    id: event_configuration_id,
                    type: 'CustomEventConfigurations',
                  }) as const,
              ),
              { id: 'LIST', type: 'CustomEventConfigurations' },
            ]
          : [{ id: 'LIST', type: 'CustomEventConfigurations' }],
      query: ({ draft_mode }) => ({
        params: { draft_mode },
        url: `${SOLVE_V2_BASE_URL}/event-configurations`,
      }),
    }),
    getEventConfigurationUsages: builder.query<
      EventConfigurationUsagesResponse,
      void
    >({
      providesTags: ['CustomEventConfigurationUsages'],
      query: () => ({
        url: `${SOLVE_V2_BASE_URL}/event-configurations/usages`,
      }),
    }),
    publishEventConfiguration: builder.mutation<EventConfiguration, string>({
      invalidatesTags: (result, error, id) => [
        { id, type: 'CustomEventConfigurations' },
      ],
      query: event_configuration_id => ({
        method: 'PUT',
        url: `${SOLVE_V2_BASE_URL}/event-configuration/${event_configuration_id}/publish`,
      }),
    }),
    updateEventConfiguration: builder.mutation<
      EventConfiguration,
      { data: EventConfigurationBase; event_configuration_id: string }
    >({
      invalidatesTags: (result, error, { event_configuration_id }) => [
        { id: event_configuration_id, type: 'CustomEventConfigurations' },
        'Tools',
        'AutoflowWorkflow',
      ],
      query: ({ data, event_configuration_id }) => ({
        body: data,
        method: 'PUT',
        url: `${SOLVE_V2_BASE_URL}/event-configuration/${event_configuration_id}`,
      }),
    }),
  }),
});

export const {
  useCreateEventConfigurationMutation,
  useDeleteEventConfigurationMutation,
  useDuplicateEventConfigurationMutation,
  useGetEventConfigurationByIdQuery,
  useGetEventConfigurationsQuery,
  useGetEventConfigurationUsagesQuery,
  usePublishEventConfigurationMutation,
  useUpdateEventConfigurationMutation,
} = customEventsApi;
